import { apiFetch } from "./api";
import { stringifySnake } from "./serialization";
import { getToken, refreshSession, clearToken } from "./auth";
import { API_BASE } from "./apiBase";
import { getApiErrorMessage } from "./errorMessages";

export function fetchMaintenance(category, vehicleId) {
  const params = new URLSearchParams();
  if (category) params.set("category", category);
  if (vehicleId) params.set("vehicleId", vehicleId);
  const query = params.toString() ? `?${params.toString()}` : "";
  return apiFetch(`/api/v1/maintenance${query}`);
}

export function fetchUpcomingMaintenance(category, vehicleId) {
  const params = new URLSearchParams();
  if (category) params.set("category", category);
  if (vehicleId) params.set("vehicleId", vehicleId);
  const query = params.toString() ? `?${params.toString()}` : "";
  return apiFetch(`/api/v1/maintenance/recurring${query}`);
}

export function createMaintenance(payload) {
  const body = normalizePayload(payload);
  return apiFetch("/api/v1/maintenance", {
    method: "POST",
    body: stringifySnake(body),
  });
}

export function updateMaintenance(id, payload) {
  const body = normalizePayload(payload);
  return apiFetch(`/api/v1/maintenance/${id}`, {
    method: "PATCH",
    body: stringifySnake(body),
  });
}

export function deleteMaintenance(id) {
  return apiFetch(`/api/v1/maintenance/${id}`, {
    method: "DELETE",
  });
}

export async function uploadMaintenanceTickets(id, tickets) {
  const payload = { tickets };
  const response = await fetch(`${API_BASE}/api/v1/maintenance/${id}/tickets`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
    },
    body: stringifySnake(payload),
    credentials: "include",
  });

  if (response.status === 401 || response.status === 403) {
    const refreshed = await refreshSession();
    if (refreshed) {
      return uploadMaintenanceTickets(id, tickets);
    }
    clearToken();
  }

  if (!response.ok) {
    const message = await safeErrorMessage(response);
    throw new Error(message || "No se pudieron subir los tickets.");
  }

  return response.json();
}

export function deleteMaintenanceTicket(id, resourceId) {
  return apiFetch(`/api/v1/maintenance/${id}/tickets/${resourceId}`, {
    method: "DELETE",
  });
}

export function fetchMaintenanceTypes(category) {
  const params = new URLSearchParams();
  if (category) params.set("category", category);
  const query = params.toString() ? `?${params.toString()}` : "";
  return apiFetch(`/api/v1/maintenance/types${query}`);
}

export function createMaintenanceType(payload) {
  return apiFetch("/api/v1/maintenance/types", {
    method: "POST",
    body: stringifySnake(payload),
  });
}

export function updateMaintenanceType(id, payload) {
  return apiFetch(`/api/v1/maintenance/types/${id}`, {
    method: "PATCH",
    body: stringifySnake(payload),
  });
}

export function deleteMaintenanceType(id) {
  return apiFetch(`/api/v1/maintenance/types/${id}`, {
    method: "DELETE",
  });
}

export function createRecurringMaintenance(payload) {
  return apiFetch("/api/v1/maintenance/recurring", {
    method: "POST",
    body: stringifySnake(payload),
  });
}

export function updateRecurringMaintenance(id, payload) {
  return apiFetch(`/api/v1/maintenance/recurring/${id}`, {
    method: "PATCH",
    body: stringifySnake(payload),
  });
}

export function deleteRecurringMaintenance(id) {
  return apiFetch(`/api/v1/maintenance/recurring/${id}`, {
    method: "DELETE",
  });
}

async function safeErrorMessage(response) {
  try {
    const data = await response.json();
    return getApiErrorMessage(data, response.status);
  } catch (_) {
    return null;
  }
}

function normalizePayload(payload) {
  if (!payload) return payload;
  const { performedAt, costEur, ...rest } = payload;
  return {
    ...rest,
    performed_at: performedAt ?? rest.performed_at,
    cost_eur: costEur ?? rest.cost_eur,
  };
}
